import { create } from 'zustand';
import { getUserGymId } from '@/utils/gymAuth';
import { getClients } from '@/services/clientsService';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string | null;
}

interface RawClient {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

interface UsersState {
  users: User[];
  loadedGymId: string | null;
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  reset: () => void;
}

// Numéro de la dernière requête lancée : seule sa réponse peut s'appliquer.
// Empêche une réponse tardive d'une autre salle d'écraser la liste courante.
let requestSeq = 0;

export const useUsersSupabaseStore = create<UsersState>((set, get) => ({
  users: [],
  loadedGymId: null,
  isLoading: false,
  error: null,

  reset: () => {
    requestSeq++;
    set({ users: [], loadedGymId: null, isLoading: false, error: null });
  },

  fetchUsers: async () => {
    const gymId = getUserGymId();
    const seq = ++requestSeq;

    if (!gymId) {
      set({ users: [], loadedGymId: null, isLoading: false, error: null });
      return;
    }

    // Liste d'une autre salle en mémoire : on la vide immédiatement.
    if (get().loadedGymId !== gymId) set({ users: [], loadedGymId: null });
    set({ isLoading: true, error: null });

    const isCurrent = () => seq === requestSeq && getUserGymId() === gymId;

    try {
      const clients = (await getClients(gymId)) as RawClient[];
      if (!isCurrent()) return;
      const users: User[] = (clients ?? [])
        .map((c) => ({
          id: c.id,
          firstName: c.firstName || '',
          lastName: c.lastName || '',
          email: c.email || undefined,
          phone: c.phone ?? null,
        }))
        .sort((a, b) => a.firstName.toLowerCase().localeCompare(b.firstName.toLowerCase()));
      set({ users, loadedGymId: gymId, isLoading: false });
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching users:', error);
      set({ users: [], loadedGymId: null, isLoading: false, error: 'Failed to fetch users' });
    }
  },
}));
